import { StorageAdapter } from '../storage/StorageAdapter'

/**
 * success-write.js — el NUCLEO ATOMICO de exito del push (PUSH-04).
 *
 * Cuando un handler envia con exito un op del outbox, hay TRES efectos que deben quedar
 * consistentes o NINGUNO:
 *   1. id_mapping(entity, client_uuid, server_id)  — para reescribir refs y NO reenviar el create.
 *   2. las marcas meta de idempotencia (occ-closed:/tcreated:/tedit:/tstatus:/evsent:).
 *   3. outbox.status='done' de la fila.
 * Si quedara la marca meta escrita pero el id_mapping perdido (o viceversa) tras un crash, un
 * re-send podria DUPLICAR el registro de servidor (la falla de PUSH-04). Por eso los tres se
 * escriben en UNA sola `db.transaction(...)`.
 *
 * POR QUE SE RE-EMITE EL SQL INLINE (no se llaman los repos singletons): IdMappingRepository.
 * setMapping y MetaRepository.put cada uno abre su PROPIO `StorageAdapter.run(...)` top-level
 * (una mini-transaccion independiente). Llamarlos aqui daria 3 escrituras top-level separadas
 * — exactamente la ventana de crash que PUSH-04 cierra. Para preservar la atomicidad se re-emite
 * el MISMO SQL (identico al de los repos) DENTRO de la unica tx, via `handle.run(sql, vals, false)`
 * (transaction=false: el handle ya es dueño del BEGIN; misma disciplina no-doble-BEGIN de F2).
 *
 * COSTURA db inyectable (default = StorageAdapter real): produccion omite el 2o arg (cero cambio);
 * el gate de test inyecta un ejecutor sql.js con la misma superficie transaction(fn)/handle.run.
 */

/**
 * Escribe id_mapping + marcas meta + outbox.status='done' en UNA transaccion (PUSH-04).
 *
 * @param {object} args
 * @param {number} args.seq              seq de la fila de outbox a finalizar.
 * @param {string} [args.entity]         'task' | 'occurrence' (requerido si hay serverId).
 * @param {string} [args.clientUuid]     client_uuid de la entidad (requerido si hay serverId).
 * @param {number} [args.serverId]       server_id devuelto por el backend (null = sin mapping).
 * @param {string[]} [args.metaKeys]     claves meta a marcar (default []).
 * @param {object} [exec]
 * @param {object} [exec.db]             ejecutor inyectable (default StorageAdapter real).
 * @returns {Promise<void>}
 */
export async function commitSuccess (
  { seq, entity, clientUuid, serverId = null, metaKeys = [] } = {},
  { db = StorageAdapter } = {}
) {
  await db.transaction(async (handle) => {
    // 1) id_mapping UPSERT (idempotente) — solo si hay server_id + client_uuid.
    //    SQL IDENTICO a IdMappingRepository.setMapping (re-emitido inline para la atomicidad).
    if (serverId != null && clientUuid) {
      await handle.run(
        'INSERT INTO id_mapping (entity, client_uuid, server_id) VALUES (?,?,?) ' +
        'ON CONFLICT(entity, client_uuid) DO UPDATE SET server_id = excluded.server_id',
        [entity, clientUuid, serverId],
        false
      )
    }

    // 2) marcas meta UPSERT (idempotente). SQL IDENTICO a MetaRepository.put.
    for (const key of metaKeys) {
      await handle.run(
        "INSERT INTO meta (key, value) VALUES (?, '1') " +
        'ON CONFLICT(key) DO UPDATE SET value = excluded.value',
        [key],
        false
      )
    }

    // 3) flip de la fila de outbox a done (limpia last_error). Guard status='in_flight' [review Hugo #11]:
    //    si apply-server-rows ya canceló este op (server-deleted -> done) mientras estaba en vuelo, el
    //    flip queda no-op y NO revive la fila; el id_mapping/meta de arriba son idempotentes e inocuos.
    await handle.run(
      "UPDATE outbox SET status='done', done_at=?, last_error=NULL WHERE seq=? AND status='in_flight'",
      [Date.now(), seq],
      false
    )
  })
}
